import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faCalendar, faUsers, faStar, faShieldAlt, faHeartbeat, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { Modal } from './Modal';
import './HelpModal.css';

export const DashboardHelpModal: React.FC<{ isOpen: boolean; onClose: () => void; }> = ({ isOpen, onClose }) => (
    <Modal isOpen={isOpen} onClose={onClose} className="help-modal">
        <h2><FontAwesomeIcon icon={faChartLine} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Dashboard Guide</h2>
        <p style={{ fontSize: '1.1em', marginBottom: '24px' }}>Welcome to your command center! 🏟️ This is where you'll manage everything about your team - from weekly matches to long-term strategy. Here's what you need to know:</p>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faCalendar} style={{ marginRight: '8px', color: 'var(--info-color)' }} />Play Week Button</h3>
            <p><strong>What it does:</strong> Hit this button to advance one week and let the football magic happen! ⚽</p>
            <p><strong>Here's what the game processes:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li>All scheduled fixtures are simulated (your matches + rivals)</li>
                <li>Player fitness and form adjust based on performance</li>
                <li>Training effects develop your squad's skills</li>
                <li>Transfer market activity - clubs buying/selling</li>
                <li>Youth academy develops your future superstars</li>
            </ul>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Pro tip: Check your Inbox for injuries, morale issues, and suspensions before clicking "Play Week" so you can adjust your tactics!</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faUsers} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Overview Tab</h3>
            <p><strong>What you see:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Next Match:</strong> Your upcoming fixture details and key stats</li>
                <li><strong>Team Metrics:</strong> Squad strength, average age, total value</li>
                <li><strong>Recent Form:</strong> Your last 5 match results</li>
                <li><strong>Upcoming Fixtures:</strong> Schedule for the next few weeks</li>
            </ul>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faHeartbeat} style={{ marginRight: '8px', color: 'var(--negative-color)' }} />Lists Tab</h3>
            <p><strong>Quick access to:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Injured Players:</strong> See who's unavailable and recovery time</li>
                <li><strong>Contract Expiry:</strong> Players leaving soon (renewals needed)</li>
                <li><strong>Suspended Players:</strong> Unavailable due to red cards</li>
                <li><strong>Recent News:</strong> Important game and player news</li>
            </ul>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Check injuries and contracts regularly to avoid squad issues</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faStar} style={{ marginRight: '8px', color: 'var(--warning-color)' }} />Key Metrics</h3>
            <p><strong>Team Strength:</strong> The combined rating of your starting XI. Higher numbers mean a stronger team, so aim to upgrade your weakest positions!</p>
            <p><strong>Fitness:</strong> Average fitness level of your squad (0-100%). This is crucial - tired players perform worse and get injured more easily.</p>
            <p><strong>Form:</strong> Your team's recent performance trend (negative = struggling, positive = momentum). Great form can carry you through tough matches!</p>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Keep average fitness above 60 to avoid injuries. Below 50? Time to rotate your squad!</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Panel Management</h3>
            <p><strong>Customize your dashboard view:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li>Click the chevron (▼/►) icon to collapse/expand panels</li>
                <li>Your preferences are saved automatically</li>
                <li>Panels remember their state when you return to the dashboard</li>
                <li>Each player detail page remembers its panel states independently</li>
            </ul>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Collapse less-used panels to focus on key information</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Winning Strategy Tips</h3>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li>📊 <strong>Plan Ahead:</strong> Check upcoming opponents in the Fixtures tab. Big match coming up? Rotate the squad before it!</li>
                <li>💪 <strong>Manage Fitness:</strong> Rotate players on easy weeks to keep everyone fresh. A tired squad is an injury-prone squad.</li>
                <li>😊 <strong>Keep Morale High:</strong> Deal with morale issues immediately - they'll tank team chemistry fast!</li>
                <li>🛡️ <strong>Defensive Depth:</strong> Keep 2-3 quality defenders on the bench. Defensive injuries can derail a season.</li>
                <li>📰 <strong>Stay Informed:</strong> Read your news regularly! Contract expirations, injury updates, and transfer tips are all there.</li>
                <li>🤖 <strong>Get Help:</strong> Use the AI Assistant Manager for tactical suggestions - especially before big matches!</li>
            </ul>
        </div>

        <div className="modal-actions">
            <button className="button primary" onClick={onClose}>Ready to Manage!</button>
        </div>
    </Modal>
);
